/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { CustomFieldDefinition, CustomSelectBrandsFieldDefinition } from "@eisinc/CustomFieldDefinition";
import Zone from "@insite/client-framework/Components/Zone";
import { PageDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import {
    CategoriesFieldDefinition,
    DateTimeFieldDefinition,
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    RichTextFieldDefinition,
    SystemListDropDownFieldDefinition,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import MobiusPage from "@insite/mobius/Page";
import * as React from "react";

const enum fields {
    categories = "categories",
    brands = "brands",
    solutions = "solutions",
    publishDate = "publishDate",
    expirationDate = "expirationDate",
    blogSummary = "blogSummary",
}

interface BlogPageProps extends PageProps {
    fields: {
        [fields.categories]: CategoriesFieldDefinition;
        [fields.brands]: CustomSelectBrandsFieldDefinition;
        [fields.solutions]: SystemListDropDownFieldDefinition;
        [fields.publishDate]: DateTimeFieldDefinition;
        [fields.expirationDate]: DateTimeFieldDefinition;
        [fields.blogSummary]: RichTextFieldDefinition;
    };
}

type OwnProps = BlogPageProps & PageProps;

const BlogPage: React.FC<OwnProps> = ({ id }) => {
    return (
        <MobiusPage>
            <Zone contentId={id} zoneName="Content" />
            <AddToListModal />
        </MobiusPage>
    );
};

export const definition: PageDefinition<CustomFieldDefinition> = {
    hasEditableUrlSegment: true,
    hasEditableTitle: true,
    pageType: "Content",
    fieldDefinitions: [
        ExcludeFromNavigation,
        ExcludeFromSignInRequired,
        HideFooter,
        HideFromSearchEngines,
        HideFromSiteSearch,
        HideHeader,
        HorizontalRule,
        MetaDescription,
        MetaKeywords,
        OpenGraphImage,
        OpenGraphTitle,
        OpenGraphUrl,
        {
            name: fields.categories,
            displayName: "Categories",
            editorTemplate: "CategoriesField",
            defaultValue: [],
            fieldType: "General",
        },
        {
            name: fields.brands,
            displayName: "Brands",
            editorTemplate: "CustomSelectBrandsField",
            defaultValue: [],
            fieldType: "General",
        },
        {
            name: fields.solutions,
            displayName: "Solutions",
            editorTemplate: "SystemListDropDownField",
            systemListName: "Custom_Solutions",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.publishDate,
            displayName: "Publish Date",
            editorTemplate: "DateTimeField",
            isRequired: true,
            defaultValue: null,
            fieldType: "General",
        },
        {
            name: fields.expirationDate,
            displayName: "Expiration Date",
            editorTemplate: "DateTimeField",
            isRequired: true,
            defaultValue: null,
            fieldType: "General",
        },
        {
            name: fields.blogSummary,
            displayName: "Blog Summary",
            editorTemplate: "RichTextField",
            isRequired: true,
            defaultValue: "",
            fieldType: "Translatable",
        },
    ] as any,
};

const blogPageModule: PageModule = {
    component: BlogPage,
    definition: definition as any,
};

export default blogPageModule;

export const BlogPageContext = "BlogPage";
